import { Link } from 'react-router-dom';
import PublicLayout from '../components/PublicLayout';
import SEO from '../components/SEO';

const ARTICLES = [
  {
    slug: 'when-is-a-wmp-required',
    title: 'When is a Waste Management Plan Required for a DA?',
    excerpt: 'Most NSW and VIC councils require a Waste Management Plan as part of a Development Application. Here\'s a guide to when you need one and what to include.',
    category: 'Guides',
    readTime: '5 min read',
  },
  {
    slug: 'how-much-does-a-wmp-cost',
    title: 'How Much Does a Waste Management Plan Cost in Australia?',
    excerpt: 'WMP costs vary depending on project complexity, council requirements, and application type. Here\'s a breakdown of typical pricing for different project types.',
    category: 'Pricing',
    readTime: '4 min read',
  },
  {
    slug: 'fogo-requirements-multi-unit',
    title: 'FOGO Requirements for Multi-Unit Developments',
    excerpt: 'Food Organics Garden Organics (FOGO) collection is now required by many NSW and VIC councils. Here\'s what developers and architects need to know.',
    category: 'Compliance',
    readTime: '6 min read',
  },
  {
    slug: 'green-star-waste-credit-guide',
    title: 'Green Star Waste Credit Guide — Credits 7A, 8A & 22B',
    excerpt: 'A practical guide to achieving Green Star waste credits for your development. What documentation is required and how to prepare a compliant specialist plan.',
    category: 'Green Star',
    readTime: '7 min read',
  },
  {
    slug: 'waste-room-sizing-guide',
    title: 'How to Size a Waste Room for Multi-Unit Developments',
    excerpt: 'Getting waste room sizing right at the design stage avoids costly redesigns later. Here are the key dimensions, clearances, and equipment considerations.',
    category: 'Design',
    readTime: '5 min read',
  },
  {
    slug: 'construction-waste-recycling-targets',
    title: 'Construction Waste Recycling Targets — What You Need to Know',
    excerpt: 'Most councils require 80%+ material recovery for demolition and construction waste. Here\'s how to set realistic targets and document your compliance.',
    category: 'D&CWMP',
    readTime: '4 min read',
  },
];

const CATEGORIES = ['All', 'Guides', 'Pricing', 'Compliance', 'Green Star', 'Design', 'D&CWMP'];

export default function Resources() {
  return (
    <PublicLayout>
      <SEO
        title="Resources — Waste Management Plan Guides & Insights"
        description="Practical guides, compliance advice, and industry insights for waste management planning in Australia. Learn about WMP requirements, costs, Green Star credits, FOGO compliance, and more."
        keywords="waste management plan guide, WMP requirements Australia, waste management plan cost, Green Star waste credit, FOGO requirements, construction waste recycling, waste room sizing"
        canonicalPath="/resources"
      />

      {/* Hero */}
      <section className="py-16 lg:py-20 bg-[#F8FAFC]" data-testid="resources-hero">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-2xl">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600 mb-3">RESOURCES &amp; INSIGHTS</div>
            <h1 className="text-3xl lg:text-4xl font-black tracking-tight text-[#0A2540] leading-tight mb-4" data-testid="resources-headline">
              Waste Management Planning Guides
            </h1>
            <p className="text-base text-slate-600 leading-relaxed">
              Practical guidance on waste management plan requirements, costs, compliance, and best practices for Australian property developments.
            </p>
          </div>
        </div>
      </section>

      {/* Articles Grid */}
      <section className="py-16 bg-white" data-testid="articles-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          {/* Category filter (visual only for now) */}
          <div className="flex flex-wrap gap-2 mb-10">
            {CATEGORIES.map((cat, i) => (
              <span key={i} className={`text-xs font-medium px-3 py-1.5 rounded-full cursor-pointer transition-colors ${i === 0 ? 'bg-[#0A2540] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
                {cat}
              </span>
            ))}
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {ARTICLES.map((article, i) => (
              <div key={i} className="bg-white rounded-2xl border border-slate-100 overflow-hidden shadow-[0_2px_12px_rgb(0,0,0,0.03)] hover:shadow-[0_8px_30px_rgb(0,0,0,0.07)] transition-all duration-300" data-testid={`article-${i}`}>
                <div className="h-36 bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center">
                  <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                    <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-blue-50 text-blue-700">{article.category}</span>
                    <span className="text-[10px] text-slate-400">{article.readTime}</span>
                  </div>
                  <h3 className="text-base font-bold text-[#0A2540] mb-2 leading-snug">{article.title}</h3>
                  <p className="text-sm text-slate-500 leading-relaxed">{article.excerpt}</p>
                  <p className="text-xs text-blue-600 font-medium mt-4">Coming soon</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#0A2540]" data-testid="resources-cta">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-2xl lg:text-3xl font-bold text-white mb-4 tracking-tight">Have a question about your project?</h2>
          <p className="text-slate-400 text-base mb-8 max-w-lg mx-auto">Our team can advise on WMP requirements for your specific council and project type. Get in touch for a no-obligation conversation.</p>
          <Link to="/contact" className="inline-flex px-8 py-3 rounded-full bg-white text-[#0A2540] font-semibold text-sm hover:bg-slate-100 transition-colors shadow-lg" data-testid="resources-cta-btn">
            Request a Quote
          </Link>
        </div>
      </section>
    </PublicLayout>
  );
}
